import { MessageCircle, Phone, Mail, Instagram, MapPin, ArrowUpRight, Clock } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { useSiteData } from "@/hooks/useSite";
import { LineReveal, Reveal, FadeIn } from "@/components/Motion";
import { WEEK_ORDER, DAY_LABELS, whatsappUrl } from "@/lib/site";

export default function Contatti() {
  const { settings: seoSettings } = useSiteData();
  const seo = seoSettings.seo?.contatti || {};
  useSEO({
    title: seo.title || "Contatti — Studio K Nutrition a Salerno | Nutrizionista",
    description:
      seo.description ||
      "K Nutrition, Via Santi Martiri Salernitani 15, Salerno. Telefono +39 379 316 5184, email knutrition.studio@gmail.com, Instagram @knutritionstudio. Richiedi il tuo appuntamento.",
    path: "/contatti",
  });

  const { settings } = useSiteData();
  const c = settings.contact;
  const ct = settings.content.contacts;

  const genericWa = whatsappUrl(c.whatsapp, "Buongiorno K Nutrition!\n\nVorrei ricevere alcune informazioni sui servizi e sui prezzi.\n\nGrazie!");

  return (
    <>
      <section className="pt-28 lg:pt-36 pb-12" data-testid="contatti-header">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-12">
          <FadeIn>
            <p className="flex items-center gap-3 text-[11px] uppercase tracking-widest2 text-deeptaupe font-medium">
              <span className="w-10 h-px bg-taupe inline-block" aria-hidden="true" />
              Contatti
            </p>
          </FadeIn>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight mt-6">
            <LineReveal delay={0.2}>{ct.title.split(" ")[0]}</LineReveal>
          </h1>
          <FadeIn delay={0.5}>
            <p className="text-base lg:text-lg leading-relaxed text-charcoal/75 max-w-2xl mt-6">{ct.text}</p>
          </FadeIn>
        </div>
      </section>

      <section className="pb-16" data-testid="contatti-body">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* contacts */}
          <div className="lg:col-span-5">
            <Reveal>
              <div className="bg-white rounded-[28px] shadow-soft p-8 lg:p-10 space-y-7" data-testid="contatti-cards">
                <div>
                  <p className="text-[11px] uppercase tracking-widest2 text-taupe font-medium mb-3">Lo studio</p>
                  <p className="font-display text-2xl leading-snug">
                    K Nutrition
                    <br />
                    Dott.ssa Francesca Notarfrancesco
                  </p>
                  <p className="mt-3 flex items-start gap-3 text-charcoal/80 text-sm leading-relaxed">
                    <MapPin size={16} className="text-taupe mt-0.5 shrink-0" />
                    <span>
                      {c.addressStreet}
                      <br />
                      {c.addressCity}, {c.addressCountry}
                    </span>
                  </p>
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(c.mapsQuery)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contatti-directions-link"
                    className="link-underline inline-flex items-center gap-1.5 mt-3 text-sm text-deeptaupe font-medium"
                  >
                    Indicazioni stradali <ArrowUpRight size={14} />
                  </a>
                </div>

                <div className="border-t border-taupe/20 pt-7 space-y-4">
                  <a
                    href={`tel:${c.phoneHref}`}
                    data-testid="contatti-phone-link"
                    className="flex items-center gap-4 group"
                  >
                    <span className="w-11 h-11 rounded-full bg-peach flex items-center justify-center transition-colors duration-300 group-hover:bg-taupe group-hover:text-ivory">
                      <Phone size={17} strokeWidth={1.5} />
                    </span>
                    <span>
                      <span className="block text-[11px] uppercase tracking-widest2 text-taupe">Telefono</span>
                      <span className="text-charcoal group-hover:text-deeptaupe transition-colors">{c.phone}</span>
                    </span>
                  </a>
                  <a
                    href={`mailto:${c.email}`}
                    data-testid="contatti-email-link"
                    className="flex items-center gap-4 group"
                  >
                    <span className="w-11 h-11 rounded-full bg-peach flex items-center justify-center transition-colors duration-300 group-hover:bg-taupe group-hover:text-ivory">
                      <Mail size={17} strokeWidth={1.5} />
                    </span>
                    <span>
                      <span className="block text-[11px] uppercase tracking-widest2 text-taupe">Email</span>
                      <span className="text-charcoal group-hover:text-deeptaupe transition-colors">{c.email}</span>
                    </span>
                  </a>
                  <a
                    href={c.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contatti-instagram-link"
                    className="flex items-center gap-4 group"
                  >
                    <span className="w-11 h-11 rounded-full bg-peach flex items-center justify-center transition-colors duration-300 group-hover:bg-taupe group-hover:text-ivory">
                      <Instagram size={17} strokeWidth={1.5} />
                    </span>
                    <span>
                      <span className="block text-[11px] uppercase tracking-widest2 text-taupe">Instagram</span>
                      <span className="text-charcoal group-hover:text-deeptaupe transition-colors">{c.instagramHandle}</span>
                    </span>
                  </a>
                </div>

                <a
                  href={genericWa}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contatti-whatsapp-button"
                  className="flex items-center justify-center gap-3 w-full rounded-full bg-deeptaupe text-ivory text-sm uppercase tracking-widest2 font-medium px-8 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:bg-charcoal"
                >
                  <MessageCircle size={17} /> Scrivici su WhatsApp
                </a>
              </div>
            </Reveal>
          </div>

          {/* map + hours */}
          <div className="lg:col-span-7 space-y-8">
            <Reveal delay={0.1}>
              <div className="rounded-3xl overflow-hidden border border-taupe/25 h-[320px] lg:h-[420px]" data-testid="contatti-map">
                <iframe
                  title="Mappa: K Nutrition, Via Santi Martiri Salernitani 15, Salerno"
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(c.mapsQuery)}&z=16&output=embed`}
                  className="w-full h-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="bg-white rounded-[28px] shadow-soft p-8" data-testid="contatti-hours">
                <h2 className="font-display text-2xl lg:text-3xl mb-6 flex items-center gap-3">
                  <Clock size={20} strokeWidth={1.5} className="text-taupe" /> Orari dello studio
                </h2>
                <ul className="space-y-2.5 text-[15px]">
                  {WEEK_ORDER.map((day) => {
                    const windows = (settings.hours || {})[day] || [];
                    return (
                      <li key={day} className="flex justify-between gap-6 border-b border-taupe/15 pb-2.5">
                        <span className="font-medium">{DAY_LABELS[day]}</span>
                        {windows.length ? (
                          <span data-testid={`hours-${day}`}>{windows.map((w) => `${w.start}–${w.end}`).join(" · ")}</span>
                        ) : (
                          <span className="text-charcoal/45 text-right" data-testid={`hours-${day}`}>
                            Non disponibile
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
                <p className="mt-5 text-[13px] leading-relaxed text-charcoal/70">
                  Orari delle finestre di richiesta per appuntamenti in studio. Le consulenze online hanno orari in
                  definizione.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
